/**
 * auctionSetup.js — Schermata unica "Prepara la tua asta": blocco lista giocatori
 * (pulsante furbo) + blocco asta (nuova/carica), sostituisce il vecchio gate
 * overlay e la vecchia schermata setup a 3 card.
 */
import { escapeHtml } from '../utils/escapeHtml.js';
import {
  DEFAULT_AUCTION_NAME,
  DEFAULT_ROSA_BUDGET,
  DEFAULT_ROSA_MAX_PLAYERS,
  DEFAULT_ROSA_ROLE_TARGETS,
  listAuctions,
} from '../services/db/auctions.pocketbase.js';

export const auctionSetupMethods = {
  _updateUnifiedStartButtonState() {
    const count = this.state.allPlayers?.length || 0;
    const ready = count > 0 && !!this.state.activeAuctionId;
    this.updateStartButton(count, ready);
  },

  _renderUnifiedListBlock() {
    const el = document.getElementById('setup-list-block');
    if (!el) return;
    const count = this.state.allPlayers?.length || 0;
    const status = count > 0
      ? `Lista pronta · stagione 2025/26`
      : 'Nessuna lista caricata ancora';
    const badge = count > 0
      ? `<span class="setup-block-badge setup-block-badge--ok">${count} caricati</span>`
      : `<span class="setup-block-badge">Da caricare</span>`;
    el.innerHTML = `
      <div class="setup-block-head">
        <span class="setup-block-icon setup-block-icon--ok" aria-hidden="true"></span>
        <span class="setup-block-title">Lista giocatori</span>
        ${badge}
      </div>
      <div class="setup-list-primary">
        <button type="button" id="setup-update-list-btn" class="setup-cta setup-cta--success">Aggiorna lista giocatori</button>
        <button type="button" id="setup-manual-file-btn" class="setup-secondary-link">Carica da un file diverso</button>
      </div>
      <p class="setup-list-status" id="setup-list-status">${escapeHtml(status)}</p>
    `;
    el.querySelector('#setup-update-list-btn')?.addEventListener('click', () => this.updatePlayerListSmart());
    el.querySelector('#setup-manual-file-btn')?.addEventListener('click', () => {
      document.getElementById('sv-file')?.click();
    });
  },

  async updatePlayerListSmart() {
    const btn = document.getElementById('setup-update-list-btn');
    const statusEl = document.getElementById('setup-list-status');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sto aggiornando…'; }

    const online = await this.loadQuotazioniOnline();
    if (online.success) {
      if (btn) { btn.disabled = false; btn.textContent = original; }
      this._renderUnifiedListBlock();
      this._updateUnifiedStartButtonState();
      return;
    }

    const saved = await this.loadLastPlayerList();
    if (saved.success) {
      const reload = await this.reloadLastPlayerList();
      if (btn) { btn.disabled = false; btn.textContent = original; }
      if (reload.success) {
        this.showFileSuccess(reload.playerCount, reload.withStats);
      } else {
        this.showFileError(reload.error);
      }
      this._renderUnifiedListBlock();
      this._updateUnifiedStartButtonState();
      return;
    }

    if (btn) { btn.disabled = false; btn.textContent = original; }
    if (statusEl) {
      statusEl.innerHTML = 'Impossibile scaricare le quotazioni online e non hai ancora nessuna lista salvata. ' +
        '<button type="button" id="setup-list-error-file-btn" class="setup-secondary-link">Carica un file Excel</button>';
      statusEl.querySelector('#setup-list-error-file-btn')?.addEventListener('click', () => {
        document.getElementById('sv-file')?.click();
      });
    }
    this._updateUnifiedStartButtonState();
  },

  async _renderUnifiedAuctionBlock(defaultMode) {
    const el = document.getElementById('setup-auction-block');
    if (!el) return;
    const accessKey = this.state.userSession?.getAccessCode?.();
    let auctions = this._unifiedSetupAuctionsCache;
    if (!auctions) {
      try {
        auctions = accessKey ? await listAuctions(accessKey) : [];
        this._unifiedSetupAuctionsCache = auctions;
      } catch (err) {
        console.error('[Setup] Errore elenco aste:', err);
        auctions = [];
      }
    }
    const mode = defaultMode || (auctions.length > 0 ? 'load' : 'new');
    if (mode === 'load' && auctions.length > 0) {
      this._renderUnifiedAuctionLoadList(auctions);
    } else {
      this._renderUnifiedAuctionNewForm(auctions.length > 0);
    }
  },

  _renderUnifiedAuctionLoadList(auctions) {
    const el = document.getElementById('setup-auction-block');
    if (!el) return;
    const activeId = this.state.activeAuctionId || '';
    const rows = auctions.map((a) => {
      const selected = a.id === activeId;
      return `
      <li>
        <button type="button" class="auction-gate-list-btn${selected ? ' is-selected' : ''}" data-auction-id="${escapeHtml(a.id)}">
          <span class="auction-gate-dot" aria-hidden="true"></span>
          <strong>${escapeHtml(a.name)}</strong>
          <span class="num">${a.budget}M · max ${a.max_players}</span>
        </button>
      </li>
    `;
    }).join('');
    el.innerHTML = `
      <div class="setup-block-head">
        <span class="setup-block-icon setup-block-icon--acc" aria-hidden="true"></span>
        <span class="setup-block-title">Asta</span>
      </div>
      <ul class="auction-gate-list">${rows}</ul>
      <button type="button" class="setup-secondary-link" data-action="new-auction">+ Crea una nuova asta invece</button>
      <p class="auction-gate-error" hidden></p>
    `;
    const errorEl = el.querySelector('.auction-gate-error');
    el.querySelectorAll('[data-auction-id]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          errorEl.hidden = true;
          await this.loadAndActivateAuction(btn.dataset.auctionId);
        } catch (err) {
          console.error(err);
          errorEl.textContent = 'Caricamento fallito. Riprova.';
          errorEl.hidden = false;
        }
      });
    });
    el.querySelector('[data-action="new-auction"]')?.addEventListener('click', () => {
      this._renderUnifiedAuctionNewForm(true);
    });
  },

  _renderUnifiedAuctionNewForm(hasSavedAuctions) {
    const el = document.getElementById('setup-auction-block');
    if (!el) return;
    const backLink = hasSavedAuctions
      ? '<button type="button" class="setup-secondary-link" data-action="back-to-list">&larr; Torna alle aste salvate</button>'
      : '';
    el.innerHTML = `
      <div class="setup-block-head">
        <span class="setup-block-icon setup-block-icon--acc" aria-hidden="true"></span>
        <span class="setup-block-title">Nuova asta</span>
      </div>
      <form class="auction-cfg-form" id="auction-gate-new-form">
        <div class="auction-cfg-grid">
          <label class="auction-cfg-field auction-cfg-field--name">
            <span>Nome</span>
            <input name="name" type="text" required maxlength="80" value="${escapeHtml(DEFAULT_AUCTION_NAME)}" class="auction-cfg-input">
          </label>
          <label class="auction-cfg-field">
            <span>Budget</span>
            <input name="budget" type="number" min="0" inputmode="numeric" value="${DEFAULT_ROSA_BUDGET}" class="auction-cfg-input num">
          </label>
          <label class="auction-cfg-field">
            <span>Max giocatori</span>
            <input name="maxPlayers" type="number" min="0" inputmode="numeric" value="${DEFAULT_ROSA_MAX_PLAYERS}" class="auction-cfg-input num">
          </label>
        </div>
        <button type="submit" class="auction-gate-btn auction-gate-btn--primary">Crea e apri</button>
        <p class="auction-gate-error" hidden></p>
      </form>
      ${backLink}
    `;
    el.querySelector('[data-action="back-to-list"]')?.addEventListener('click', () => {
      this._renderUnifiedAuctionBlock('load');
    });
    el.querySelector('#auction-gate-new-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const form = e.target;
      const errorEl = form.querySelector('.auction-gate-error');
      try {
        errorEl.hidden = true;
        await this.createAndActivateAuction({
          name: form.querySelector('input[name="name"]').value,
          budget: Number(form.querySelector('input[name="budget"]').value) || 0,
          maxPlayers: Number(form.querySelector('input[name="maxPlayers"]').value) || 0,
          roleTargets: { ...DEFAULT_ROSA_ROLE_TARGETS },
        });
      } catch (err) {
        console.error(err);
        errorEl.textContent = 'Creazione fallita. Riprova.';
        errorEl.hidden = false;
      }
    });
  },

  showUnifiedSetupScreen({ defaultMode } = {}) {
    const setupScreen = document.getElementById('auction-setup-screen');
    const mainInterface = document.getElementById('auction-main-interface');
    if (setupScreen) { setupScreen.hidden = false; setupScreen.style.display = 'block'; }
    if (mainInterface) { mainInterface.hidden = true; mainInterface.style.display = 'none'; }
    const backBtn = document.getElementById('setup-back-to-lista');
    if (backBtn) {
      const canReturn = !!this.state.activeAuctionId;
      backBtn.hidden = !canReturn;
      if (!backBtn.dataset.bound) {
        backBtn.dataset.bound = '1';
        backBtn.addEventListener('click', () => {
          this.hideUnifiedSetupScreen?.();
          this.showMainInterface?.();
        });
      }
    }
    this._unifiedSetupAuctionsCache = null;
    return this._renderUnifiedSetupScreen(defaultMode);
  },

  hideUnifiedSetupScreen() {
    const setupScreen = document.getElementById('auction-setup-screen');
    if (setupScreen) { setupScreen.hidden = true; setupScreen.style.display = 'none'; }
  },

  async _renderUnifiedSetupScreen(defaultMode) {
    this._renderUnifiedListBlock();
    await this._renderUnifiedAuctionBlock(defaultMode);
    this._updateUnifiedStartButtonState();
  },

  async refreshUnifiedSetupAfterAuctionChange() {
    this._unifiedSetupAuctionsCache = null;
    const setupScreen = document.getElementById('auction-setup-screen');
    if (setupScreen && !setupScreen.hidden) {
      await this._renderUnifiedAuctionBlock('load');
    }
    this._updateUnifiedStartButtonState();
  },
};
